import {
  applyDeclaredPrimaryEvidence,
  createEmptyBusinessGraphV1,
  SitePackageIdSchema,
  type AgencyTodaySevenTierInput,
  type BusinessGraphV1,
} from "@enrein/shared-contracts";

export const FILTERABLE_GREENFIELD_VIEWS = ["businesses", "portfolio", "work"] as const;

export function isFilterableGreenfieldView(view: string, dataUnavailable: boolean): boolean {
  if (dataUnavailable) return false;
  return (FILTERABLE_GREENFIELD_VIEWS as readonly string[]).includes(view);
}

export function classifyGreenfieldListLoad(input: {
  readonly pipelineFailed: boolean;
  readonly todayFailed: boolean;
  readonly businessesFailed: boolean;
}): { readonly unavailable: boolean; readonly listsLoaded: boolean } {
  if (input.pipelineFailed) {
    return { unavailable: true, listsLoaded: false };
  }
  return { unavailable: false, listsLoaded: !input.businessesFailed };
}

export function startBuildBlockedReason(input: {
  readonly confirmationId?: string | null;
  readonly draftId?: string | null;
  readonly draftStatus?: string | null;
  readonly latestRunStatus?: string | null;
}): string | null {
  if (!input.confirmationId || !input.draftId) {
    return "Start build is closed until a confirmed candidate exists.";
  }
  if (input.draftStatus && input.draftStatus !== "CONFIRMED") {
    return "Start build is closed until the updated candidate is confirmed.";
  }
  if (input.latestRunStatus && ["QUEUED", "RUNNING", "PENDING"].includes(input.latestRunStatus)) {
    return "A build is already queued or running.";
  }
  return null;
}

/** First-build double-click keeps the same key; a new draft/hash creates a new run. */
export function confirmedBuildIdempotencyKey(input: {
  readonly projectId: string;
  readonly confirmationId: string;
  readonly draftId: string;
  readonly draftVersion: number;
  readonly draftSnapshotHash: string;
}): string {
  return `gf-build-${input.projectId}-${input.confirmationId}-${input.draftId}-v${input.draftVersion}-${input.draftSnapshotHash}`;
}

export function confirmDraftIdempotencyKey(input: {
  readonly projectId: string;
  readonly draftId: string;
  readonly draftVersion: number;
  readonly draftSnapshotHash: string;
}): string {
  return `gf-confirm-${input.projectId}-${input.draftId}-${input.draftVersion}-${input.draftSnapshotHash}`;
}

export function draftNeedsReconfirmation(
  draft: { readonly status?: string | null } | null | undefined,
): boolean {
  return Boolean(draft && draft.status !== "CONFIRMED");
}

/**
 * Confirm service compares expectedPredecessorRevisionId to the active
 * TenantProjectRevision.currentBusinessGraphRevisionId. First confirmation
 * has no active head, so this is null. Reconfirmation after activation
 * must send that exact revision.
 */
export function expectedPredecessorRevisionIdFromRoomTruth(
  truth: { readonly currentBusinessGraphRevisionId?: string | null },
): string | null {
  const value = truth.currentBusinessGraphRevisionId;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const BUSINESS_DESCRIPTION_MAX = 2000;

/**
 * Prefill the Agency-editable Business Description from the current draft only.
 * Client change-request text is reference material and must never become the value.
 */
export function currentDraftDescriptionPrefill(input: {
  readonly draftDescription?: string | null;
  readonly changeRequestMessage?: string | null;
}): string {
  void input.changeRequestMessage;
  return input.draftDescription?.trim() ?? "";
}

/**
 * Candidate review / Business confirmation target.
 * When a current draft description exists, that is what Agency is confirming.
 * Intake is first-confirm fallback only; change-request text is never the target.
 */
export function candidateConfirmationDescription(input: {
  readonly draftDescription?: string | null;
  readonly intakeDescription?: string | null;
  readonly changeRequestMessage?: string | null;
}): string {
  void input.changeRequestMessage;
  const draft = input.draftDescription?.trim() ?? "";
  if (draft) return draft;
  return input.intakeDescription?.trim() ?? "";
}

/**
 * Bounded Agency-edited description for the existing updateBusinessGraphDraft path.
 * The caller must pass the field value; this never reads change-request text.
 */
export function agencyEditedDescriptionDraftUpdate(
  description: string,
): { readonly description: string } | { readonly error: string } {
  const next = description.trim().slice(0, BUSINESS_DESCRIPTION_MAX);
  if (!next) {
    return { error: "Enter a business description before saving." };
  }
  return { description: next };
}

function namedEntries(value: unknown, prefix: string): Array<{ id: string; name: string }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item, index) => {
    if (!item || typeof item !== "object") return [];
    const rec = item as { id?: unknown; name?: unknown };
    const name = typeof rec.name === "string" ? rec.name.trim() : "";
    if (!name) return [];
    const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : `${prefix}_${index + 1}`;
    return [{ id, name }];
  });
}

export function candidateGraphFromRoomIntake(input: {
  readonly agencyId: string;
  readonly projectId: string;
  readonly businessName: string;
  readonly primaryPhone?: string | null;
  readonly primaryEmail?: string | null;
  readonly description?: string | null;
  readonly services?: unknown;
  readonly serviceAreas?: unknown;
  readonly primaryEvidenceType?: string | null;
  readonly sitePackageId?: string | null;
  readonly lensId?: "lens-flat-2" | "lens-glass";
}): BusinessGraphV1 {
  const graph = createEmptyBusinessGraphV1({
    businessGraphId: `bg_${crypto.randomUUID()}`,
    agencyId: input.agencyId,
    projectId: input.projectId,
    revisionId: `rev_${crypto.randomUUID()}`,
    businessName: input.businessName,
  });
  if (input.primaryPhone?.trim()) graph.business.primaryPhone = input.primaryPhone.trim();
  // Agency review still shows submitted intake email from room truth. Raw email must
  // not enter the generation graph — public HTML fails qa-007.privacy.pii_surface.
  if (input.description?.trim()) graph.business.description = input.description.trim();
  graph.services = namedEntries(input.services, "service").map((service) => ({
    ...service,
    restrictions: [],
  }));
  graph.operations.serviceAreas = namedEntries(input.serviceAreas, "area");
  applyDeclaredPrimaryEvidence(graph, input.primaryEvidenceType);
  if (input.sitePackageId != null) {
    const sitePackage = SitePackageIdSchema.safeParse(input.sitePackageId);
    if (!sitePackage.success) throw new Error("invalid_site_package_selection");
    graph.layout.sitePackageId = sitePackage.data;
  }
  if (input.lensId != null) {
    if (input.lensId !== "lens-flat-2" && input.lensId !== "lens-glass") {
      throw new Error("unsupported_pilot_lens");
    }
    graph.layout.lensId = input.lensId;
  }
  return graph;
}

export type PipelineTruthItem = {
  readonly projectId: string;
  readonly businessName?: string | null;
  readonly status?: string | null;
  readonly stage?: string | null;
  readonly clientLinkStatus?: string | null;
  readonly draftStatus?: string | null;
  readonly hasConfirmation?: boolean;
  readonly generating?: boolean;
  readonly latestRunStatus?: string | null;
  readonly outstandingClientAction?: boolean;
  readonly buildEligible?: boolean;
};

const GRAPH_LIFECYCLE_STATUSES = new Set([
  "PROJECT_CREATED",
  "BUSINESSGRAPH_DRAFTING",
  "BUSINESSGRAPH_VALIDATED",
  "REVISION_ACTIVE",
]);

const APPROVED_CLIENT_LINK_STATUSES = new Set([
  "APPROVED",
  "PORTAL_ACTIVE",
  "SAFE_EDIT_ACTIVE",
  "HANDOFF_READY",
]);

const PREVIEW_RUN_STATUSES = new Set([
  "QA_PASSED",
  "QA_PASSED_WITH_WARNINGS",
  "EXPORT_READY",
  "EXPORTING",
  "EXPORTED",
  "SUPERSEDED",
]);

const GENERATING_RUN_STATUSES = new Set([
  "QUEUED",
  "RUNNING",
  "PENDING",
  "GENERATING",
  "GENERATED",
  "QA_QUEUED",
  "QA_RUNNING",
]);

const FAILED_RUN_STATUSES = new Set(["FAILED", "QA_FAILED", "CANCELLED"]);

const LIST_STAGE_LABELS = {
  waiting_for_client: "Waiting for client",
  review_and_build: "Review & build",
  generating: "Generating",
  preview_live: "Preview live",
  client_approved: "Client approved",
  packaging: "Packaging",
  handoff_ready: "Handoff ready",
} as const;

export type GreenfieldListStageId = keyof typeof LIST_STAGE_LABELS;

export type GreenfieldListRowPresentation = {
  readonly statusLabel: string;
  readonly stageId: GreenfieldListStageId;
  readonly stageLabel: string;
};

function listRow(stageId: GreenfieldListStageId, statusLabel: string): GreenfieldListRowPresentation {
  return { statusLabel, stageId, stageLabel: LIST_STAGE_LABELS[stageId] };
}

/**
 * Business list / project-row presentation from pipeline facts.
 * Graph lifecycle (`REVISION_ACTIVE`) is not delivery truth after exact approval / export.
 */
export function presentGreenfieldListRow(item: PipelineTruthItem): GreenfieldListRowPresentation {
  const link = item.clientLinkStatus ?? "";
  const run = item.latestRunStatus ?? "";
  const generating = item.generating === true || GENERATING_RUN_STATUSES.has(run);
  const approved = APPROVED_CLIENT_LINK_STATUSES.has(link);
  const previewReady = PREVIEW_RUN_STATUSES.has(run) || link === "PREVIEW_READY" || link === "APPROVAL_PENDING";

  if (generating) {
    return listRow("generating", "Generating");
  }
  if (link === "HANDOFF_READY") {
    return listRow("handoff_ready", "Complete");
  }
  if (approved && (run === "EXPORTING" || run === "EXPORT_READY")) {
    return listRow("packaging", "Complete");
  }
  if (approved) {
    return listRow("client_approved", "Complete");
  }
  if (FAILED_RUN_STATUSES.has(run)) {
    return listRow("review_and_build", "Needs attention");
  }
  if (link === "CHANGE_REQUESTED") {
    return listRow("review_and_build", "Changes requested");
  }
  if (previewReady) {
    return listRow("preview_live", "Preview ready");
  }
  if (
    link === "INTAKE_SUBMITTED" ||
    item.hasConfirmation === true ||
    item.draftStatus === "CONFIRMED"
  ) {
    return listRow("review_and_build", "Review & build");
  }
  if (
    link === "INTAKE_OPEN" ||
    link === "INTAKE_IN_PROGRESS" ||
    item.status === "PROJECT_CREATED" ||
    item.stage === "waiting_for_client" ||
    !link
  ) {
    return listRow("waiting_for_client", "Waiting");
  }
  if (GRAPH_LIFECYCLE_STATUSES.has(item.status ?? "")) {
    return listRow("review_and_build", "Review & build");
  }
  const rawStatus = item.status?.trim() || "unknown";
  const rawStage = (item.stage ?? "review_and_build") as string;
  const stageId = (rawStage in LIST_STAGE_LABELS ? rawStage : "review_and_build") as GreenfieldListStageId;
  return listRow(stageId, GRAPH_LIFECYCLE_STATUSES.has(rawStatus) ? LIST_STAGE_LABELS[stageId] : rawStatus);
}

export function composeTodayInputFromPipeline(item: PipelineTruthItem): AgencyTodaySevenTierInput {
  const projectId = item.projectId;
  const linkStatus = item.clientLinkStatus ?? null;
  const intakeSubmitted = linkStatus === "INTAKE_SUBMITTED";
  const draftUnconfirmed = item.draftStatus === "DRAFT" || item.draftStatus === "SUPERSEDED";
  const confirmed =
    !draftUnconfirmed &&
    (item.hasConfirmation === true || item.draftStatus === "CONFIRMED") &&
    item.buildEligible !== false;
  const generating = item.generating === true;
  const waitingOnClient =
    !confirmed &&
    !intakeSubmitted &&
    (linkStatus === "INTAKE_OPEN" ||
      linkStatus === "INTAKE_IN_PROGRESS" ||
      !linkStatus ||
      item.status === "PROJECT_CREATED" ||
      item.stage === "waiting_for_client");
  const needsCandidateReview = intakeSubmitted && !confirmed;

  return {
    projectId,
    businessName: item.businessName,
    waitingOnClient,
    confirmedReadyToBuild: confirmed && !generating,
    qaFailure: item.latestRunStatus === "FAILED" || item.latestRunStatus === "QA_FAILED",
    qualityGateNeedsReview: item.latestRunStatus === "QA_REVIEW",
    clientReview: intakeSubmitted
      ? { projectId, clientLink: { status: "INTAKE_SUBMITTED" } }
      : linkStatus
        ? { projectId, clientLink: { status: linkStatus } }
        : null,
    lifecycle: needsCandidateReview
      ? {
          projectId,
          simplePhase: "review_and_build",
          primaryActionId: "confirm-build",
          flags: {
            qaFailure: false,
            generating,
            packaging: false,
            generationLock: generating,
          },
        }
      : null,
  };
}

export type VersionTruth = {
  readonly working: string;
  readonly clientVisible: string;
  readonly approved: string;
  readonly live: string;
};

export const ABSENT_VERSION = "none";

export function versionTruthFromRecords(input: {
  readonly workingVersionId?: string | null;
  readonly clientVisibleVersionId?: string | null;
  readonly approvedVersionId?: string | null;
  readonly liveVersionId?: string | null;
}): VersionTruth {
  const label = (value?: string | null) => {
    const trimmed = value?.trim() ?? "";
    return trimmed.length > 0 ? trimmed : ABSENT_VERSION;
  };
  return {
    working: label(input.workingVersionId),
    clientVisible: label(input.clientVisibleVersionId),
    approved: label(input.approvedVersionId),
    live: label(input.liveVersionId),
  };
}

export function approvalPreviewVersionId(input: {
  readonly previewUrl?: string | null;
  readonly clientVisibleVersionId?: string | null;
}): string | null {
  if (!input.previewUrl?.trim()) return null;
  const version = input.clientVisibleVersionId?.trim() ?? "";
  return version.length > 0 ? version : null;
}

export function approvalCopyFromRecord(input: {
  readonly approvalId?: string | null;
  readonly approvedAt?: string | null;
  readonly previewVersionId?: string | null;
}): {
  readonly condition: "READY" | "WAITING" | "COMPLETE";
  readonly showCondition: boolean;
  readonly label: string;
  readonly detail: string;
} {
  if (input.approvalId && input.approvedAt) {
    return {
      condition: "COMPLETE",
      showCondition: true,
      label: "Exact approval recorded",
      detail: `Approved ${input.approvedAt}${
        input.previewVersionId ? ` for preview ${input.previewVersionId.slice(0, 12)}…` : ""
      }.`,
    };
  }
  if (input.previewVersionId) {
    return {
      condition: "WAITING",
      showCondition: true,
      label: "Waiting for exact approval",
      detail:
        "A current preview exists. Exact approval is recorded only for that version. The client must approve in Client Hub — Studio cannot record exact approval.",
    };
  }
  return {
    condition: "READY",
    showCondition: false,
    label: "No preview to approve",
    detail: "Exact approval cannot start until a current preview exists.",
  };
}

export function shouldOfferPrepareDelivery(input: {
  readonly approvalComplete: boolean;
  readonly deliveryDueAt?: string | null;
}): boolean {
  return input.approvalComplete && !input.deliveryDueAt;
}

export function shouldOfferPackageHandoff(input: {
  readonly approvalComplete: boolean;
  readonly deliveryDueAt?: string | null;
  readonly runId?: string | null;
}): boolean {
  return Boolean(input.approvalComplete && input.deliveryDueAt && input.runId);
}

export function greenfieldTruthPollMs(
  truth: {
    readonly clientAccess?: { readonly exists?: boolean };
    readonly intake?: { readonly submitted?: boolean } | null;
    readonly versions?: { readonly clientVisible?: string | null };
    readonly approval?: { readonly approvalId?: string } | null;
  } | null,
  configuredMs: number,
): number {
  const waitingIntake = Boolean(truth?.clientAccess?.exists && !truth.intake?.submitted);
  const waitingApproval = Boolean(truth?.versions?.clientVisible && !truth.approval);
  if (waitingIntake || waitingApproval) return Math.min(configuredMs, 4000);
  return configuredMs;
}

export function changesEmptyCopy(): { readonly before: string; readonly after: string } {
  return {
    before: "No change requests yet.",
    after: "A revised preview appears here only after a client submits a structured change request.",
  };
}

export function clientAccessLabel(input: {
  readonly exists: boolean;
  readonly status?: string | null;
}): string {
  if (!input.exists) return "not created";
  return input.status?.trim() ? input.status.trim().toLowerCase().replace(/_/g, " ") : "ready";
}

/** First-time create is offered only when no active client access exists. */
export function shouldOfferCreateClientLink(exists: boolean): boolean {
  return !exists;
}

/**
 * Mid-journey recovery: when a link already exists but the one-time token is no longer shown,
 * the operator can reissue a fresh token for the same clientLinkId.
 */
export function shouldOfferReissueClientLink(exists: boolean): boolean {
  return exists;
}

export function clientLinkActionLabel(exists: boolean): string {
  return exists ? "Create new copy-link access" : "Create copy-link access";
}

export function projectOrientation(input: {
  readonly businessName?: string | null;
  readonly projectLabel?: string | null;
  readonly section?: string | null;
}): { readonly title: string; readonly context: string } {
  const business = input.businessName?.trim() || "This business";
  const project = input.projectLabel?.trim() || "Website project";
  const section = input.section?.trim();
  return {
    title: "Project room",
    context: section ? `${business} · ${project} · ${section}` : `${business} · ${project}`,
  };
}
